/**
 * This is a file containing all necessary interface to get the measurements running.
 */
// the tag name for getLogger
export const FPS_MEASUREMENT_LOGGER = "FPSMeasurement";

/**
 * Created by rkamysz on 3/9/2015.
 */

export interface IMeasurement {
    /**
     * This method is called by trigger when all conditions are fulfilled and timer isn't in <code>delayed</code> state.
     * This method should contain all code needed to run the measurement process.
     * @param payload object passed to the measurement:
     * <ul>
     * <li>manually</li>
     * <li>via configurator method <code>setPayloadAtStart()</code></li>
     * </ul>
     */
    start(payload: Object): void;

    /**
     * This method is called when:
     * <ul>
     * <li>measurement duration reached its limit</li>
     * <li>measurement is manually stopped</li>
     * <li>measurement gets interrupted by other trigger, breakpoint or watchdog</li>
     * </ul>
     * This method should contain all code needed to clean the measurement process.
     * @param payload object passed to the measurement:
     * <ul>
     * <li>manually</li>
     * <li>via configurator method <code>setPayloadAtStop()</code></li>
     * </ul>
     */
    stop(payload: Object): void;

    /**
     * This method is called when methods: <code>removeMeasurement()</code>,<code>removeMeasurementsOf()</code> are executed.
     * It must contain code to cleanup the measurement when it is no longer in use.
     */
    dispose(): void;

    /**
     * This method must return results of measurements.
     * <b>Info:</b>You may use any type for results.
     * <b>Example:</b> <a href="https://sites.google.com/a/goodgamestudios.com/tech-wiki/client-department/core-projects/core/concepts/measurement-system">see example</a>
     */
    results: Object;
}

/**
 * Created by rkamysz on 3/9/2015.
 */

export interface IMeasurementConfig {
    /**
     * Determines whether measurement can be executed by calling the <code>execute()</code> method.
     */
    canBeExecutedManually: boolean;

    /**
     * Determines whether measurement can be interrupted by other trigger or condition change.
     */
    canBeInterrupted: boolean;

    /**
     * Determines whether measurement can be interrupted when application gets deactivated.
     */
    pauseOnApplicationDeactivate: boolean;

    /**
     * Determines delay to start the measurement.
     */
    delayInMilliseconds: number;

    /**
     * Determines duration of measurement.
     */
    durationInMilliseconds: number;

    /**
     * <p>Determines minimal duration of measurement.</p>
     * <p><b>Info:</b>After measurement reaches this limit and gets interrupted, results will be treated as valid.</p>
     */
    minimalDurationInMilliseconds: number;
}

/**
 * Created by rkamysz on 3/16/2015.
 */

/**
 * MeasurementConfigurator is an tool used to configure the created measurement.
 * By using its methods, you can:
 * <ul>
 *     <li>Specify when and on which conditions the measurement should start.
 *     See <code>addTrigger()</code></li>
 *     <li>Specify when and on which conditions the measurement should be stopped.
 *     See <code>addBreakpoint()</code></li>
 *       <li>Add special command which will be launched after measurement completes.
 *       See <code>onComplete()</code></li>
 *     <li>Add special watchdogs to control behavior of the measurement.
 *     See <code>addWatchdog()</code></li>
 * </ul>
 * Each method returns object of <code>MeasurementConfigurator</code> which allowing the calls to be chained together.
 */
export interface IMeasurementConfigurator {
    /**
     * By using this method you can set gameData which will be used in all output handlers.
     * This method will be useful if you need to send some extra information( from the game)  with measurement results.
     *
     * <b>Info:</b> Each call of this method will override previous gameData.
     *
     * @param gameData
     * @return This method returns object of <code>MeasurementConfigurator</code>.
     */
    setGameData(gameData: Object): IMeasurementConfigurator;

    /**
     * With this method you can set payloads which you can use in your measurement method <code>start()</code>.
     *
     * <b>Info:</b> Each call of this method will override previous payloads.
     *
     * @param payload
     * @return This method returns object of <code>MeasurementConfigurator</code>.
     */
    setPayloadAtStart(payload: Object): IMeasurementConfigurator;

    /**
     * With this method you can set payloads which you can use in your measurement method <code>stop()</code>.
     *
     * <b>Info:</b> Each call of this method will override previous payloads.
     *
     * @param payload
     * @return This method returns object of <code>MeasurementConfigurator</code>.
     */
    setPayloadAtStop(payload: Object): IMeasurementConfigurator;

    /**
     * By using this method, you can add one or more triggers to the measurement.
     * How many triggers you add, should depend on when the measurement must start doing its job.
     *
     * <b>Example:</b> <a href="https://sites.google.com/a/goodgamestudios.com/tech-wiki/client-department/core-projects/core/concepts/measurement-system">see example</a>
     *
     * <p><b>Info:</b> This method contains validation of trigger type.
     * If you pass the trigger with wrong type, method will throw Error</p>
     * <p><b>Info:</b> <code>MeasurementSystem</code> also contains validation of conditions.
     * If you pass condition which doesn't return <code>boolean</code> value, it will be treated as a failure.</p>
     *
     * @param trigger is an object that executes the measurement.
     * <ul>
     *     <li>object of <code>Signal</code></li>
     *     <li>object which extends <code>MeasurementPin</code></li>
     * </ul>
     * @param conditions which must be fulfilled to use the trigger.
     * Condition must be:
     * <ul>
     *     <li><code>boolean</code> value</li>
     *     <li>function which returns <code>boolean</code> value</li>
     *     <li>object or class which implements <code>ICondition</code></li>
     * </ul>
     *
     * @return This method returns object of <code>MeasurementConfigurator</code>.
     *
     */
    addTrigger(trigger: Object, conditions: any[]): IMeasurementConfigurator;

    /**
     * By using this method, you can add one or more breakpoints to the measurement.
     * How many breakpoints you add, should depend on when the measurement must stop doing its job.
     *
     * @param breakpoint is an object that stops the measurement.
     * @param conditions which must be fulfilled to use the breakpoint.
     * Condition must be:
     * <ul>
     *     <li><code>boolean</code> value</li>
     *     <li>function which returns <code>boolean</code> value</li>
     *     <li>object or class which implements <code>ICondition</code></li>
     * </ul>
     *
     * @return This method returns object of <code>MeasurementConfigurator</code>.
     */
    addBreakpoint(breakpoint: Object, conditions: any[]): IMeasurementConfigurator;

    /**
     * By using this method, you can add one or more watchdogs to the measurement.
     *
     * The main role for watchdog is to control measurement behavior.
     * If measurement should change its status in some special cases, you can define those in watchdog.
     *
     * <b>Example:</b> <a href="https://sites.google.com/a/goodgamestudios.com/tech-wiki/client-department/core-projects/core/concepts/measurement-system">see example</a>
     *
     * <p><b>Info:</b>You should provide conditions for adding the watchdog to the measurement.
     * Otherwise it will be created and launched right away.</p>
     * <p><b>Info:</b> This method contains validation of watchdog type.
     * If you pass the trigger with wrong type, method will throw Error</p>
     * <p><b>Info:</b> <code>MeasurementSystem</code> also contains validation of conditions.
     * If you pass condition which doesn't return <code>boolean</code> value, it will be treated as a failure.</p>
     *
     * @param watchdog instance or class which extends <code>MeasurementWatchdog</code>
     *
     * @param conditions which must be fulfilled to use the watchdog.
     * Condition must be:
     * <ul>
     *     <li><code>boolean</code> value</li>
     *     <li>function which returns <code>boolean</code> value</li>
     * </ul>
     *
     * @return This method returns object of <code>MeasurementConfigurator</code>.
     *
     */
    addWatchdog(watchdog: Object, conditions: any[]): IMeasurementConfigurator;

    /**
     * With this method you can add handlers for your measurement results.
     * The amount of handlers should depend on where the results should be reported.
     *
     * @param handler is the object which parses and sends the measurement results and game data to the receiver.
     * @param conditions which must be fulfilled to use the handler.
     * Condition must be:
     * <ul>
     *     <li><code>boolean</code> value</li>
     *     <li>function which returns <code>boolean</code> value</li>
     *     <li>object or class which implements <code>ICondition</code></li>
     * </ul>
     * @return This method returns object of <code>MeasurementConfigurator</code>.
     */
    addOutputHandler(handler: Object, conditions?: any[]): IMeasurementConfigurator;
}

export interface IMeasurementFacade {
    /**
     * With this method you can manually start the measurement.
     * Only condition is that in measurement config you must set <code>canBeExecutedManually</code> as <code>true</code>
     * You can also pass some payloads which will be used only with this call.
     * @param payload payloads for the measurement.
     */
    start(payload?: Object ): void;

    /**
     * With this method you can manually stop the measurement only if it was started manually.
     * You can also pass some payloads which will be used only with this call.
     * @param payload payloads for the measurement.
     */
    stop(payload?: Object ): void;

    /**
     * With this method you can turn on the measurement if it was deactivated before.
     */
    activate(): void;

    /**
     * With this method you can temporary turn off the measurement.
     */
    deactivate(): void;

    /**
     * This method returns the configurator of the measurement
     * @see IMeasurementConfigurator
     */
    configurator: IMeasurementConfigurator;

    /**
     * With this method you can check if measurement is currently running.
     */
    isRunning: boolean;

    /**
     * With this method you can check if measurement is activated.
     */
    isActive: boolean;
}

/**
 * Created by rkamysz on 3/16/2015.
 */

export interface ICondition {
    /**
     * This method should contain the settings you need to check the condition.
     *
     * @param payloads
     */
    initialize(payloads: any[]): void;

    /**
     * This method must return the outcome of the verification
     */
    isFulfilled: boolean;
}

/**
 * Created by Radoslaw on 2015-03-14.
 */

export interface IMediatorComponent {
}

export interface IOutputHandler extends IMediatorComponent {
    parse(measurementDuration: number, measurementResults: Object, gameData: Object): void;

    send(): void;
}
